import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Calendar, Mail, Phone, Globe } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 font-semibold mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-emerald-100">
          {/* Header */}
          <div className="border-b border-gray-100 pb-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
                <p className="text-emerald-600 font-medium">Journi (Pvt) Ltd</p>
              </div>
            </div>
            <p className="text-gray-600 font-medium mb-4">How Journi collects, uses, and protects your personal data</p>
            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                Effective Date: June 2026 | Last Updated: June 2026
              </span>
            </div>
          </div>

          {/* Quick Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100/50">
            <a href="https://journi.pk" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-emerald-700 hover:underline">
              <Globe className="w-4 h-4 text-emerald-600" />
              journi.pk
            </a>
            <a href="mailto:subscribe@journi.pk" className="flex items-center gap-2 text-emerald-700 hover:underline">
              <Mail className="w-4 h-4 text-emerald-600" />
              subscribe@journi.pk
            </a>
            <a href="https://wa.me/923312312345" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-emerald-700 hover:underline">
              <Phone className="w-4 h-4 text-emerald-600" />
              03312312345
            </a>
          </div>

          {/* Document Content */}
          <div className="space-y-8 text-gray-700 leading-relaxed">
            
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">1. Introduction</h2>
              <p className="mb-3">
                Welcome to Journi, operated by Journi (Pvt) Ltd ('We', 'us', or 'our'). Journi is a carpooling platform connecting daily commuters in Pakistan through corridor-based ride matching technology.
              </p>
              <p className="mb-3">
                This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use the Journi platform at journi.pk and any related services (collectively, the 'Platform').
              </p>
              <p className="mb-3">
                By registering on or using the Journi Platform, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree, please do not use the Platform.
              </p>
              <p>
                This policy is compliant with the Personal Data Protection Act 2023 (PDPA) of Pakistan and applicable regulations issued by the National Commission for Personal Data Protection (NCPDP).
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">2.1 Information You Provide Directly</h3>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Full name</li>
                    <li>Mobile phone number (used as primary account identifier)</li>
                    <li>Gender</li>
                    <li>Profile photograph (optional)</li>
                    <li>CNIC number and copies of CNIC front and back (for verification, when submitted)</li>
                    <li>Selfie photograph (for identity verification)</li>
                    <li>Driver's licence number and photograph (for driver verification, when submitted)</li>
                    <li>Vehicle registration details, make, model, year, and number plate (for drivers)</li>
                    <li>Emergency contact name and phone number</li>
                    <li>Password (stored as a one-way cryptographic hash – we never store plaintext passwords)</li>
                    <li>Referral codes entered or generated</li>
                    <li>Payment confirmation screenshots submitted via WhatsApp for subscription activation</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">2.2 Information Generated Through Platform Use</h3>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Pickup and drop-off locations entered when searching or posting rides</li>
                    <li>Saved locations (Home, Work, and custom locations)</li>
                    <li>Ride history – rides offered, rides taken, ride requests made</li>
                    <li>Contact exchange history – when you exchange contact details with other users</li>
                    <li>Messages sent to other users through the Platform</li>
                    <li>Preferences selected – non-smoking, female friendly, climate control, child friendly</li>
                    <li>Ratings and reviews submitted or received</li>
                    <li>Notifications read or dismissed</li>
                    <li>Device information – device type, operating system, browser type</li>
                    <li>IP address and approximate location at time of login</li>
                    <li>Usage data – pages visited, features used, time spent on Platform</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">2.3 Information From Third Parties</h3>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Google Maps API – location geocoding and route data when you search for rides or post a ride. Google's privacy policy applies to data processed by Google Maps.</li>
                    <li>Firebase – push notification delivery. Firebase processes device tokens to deliver notifications.</li>
                    <li>WhatsApp – when you use our WhatsApp contact number for payment confirmation or support, WhatsApp's privacy policy applies.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">3.1 To Provide the Service</h3>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Match riders with drivers through Polypoints corridor matching algorithm</li>
                    <li>Display ride listings relevant to your search location and direction</li>
                    <li>Enable contact exchange between matched drivers and riders</li>
                    <li>Process referral codes and award bonus contact exchanges</li>
                    <li>Send ride-related notifications – new matches, request approvals, cancellations</li>
                    <li>Manage your account, subscription status, and exchange credits</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">3.2 For Safety and Trust</h3>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Verify identity through CNIC, licence, and vehicle documentation</li>
                    <li>Display verified badges on profiles that have completed verification</li>
                    <li>Investigate reports of misconduct, harassment, or fraudulent activity</li>
                    <li>Enforce our Terms of Service and Community Guidelines</li>
                    <li>Prevent, detect, and respond to fraud, abuse, and security incidents</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">3.3 For Platform Improvement</h3>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Analyse usage patterns to improve features and user experience</li>
                    <li>Debug technical issues and fix errors</li>
                    <li>Conduct internal research and analytics</li>
                    <li>Develop new features such as stop-to-stop routes and monthly subscriptions</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">3.4 For Communications</h3>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Send service announcements, security alerts, and account notifications</li>
                    <li>Respond to support requests submitted via WhatsApp or email</li>
                    <li>Send promotional communications – you may opt out at any time</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">3.5 Legal Compliance</h3>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Comply with applicable Pakistani law, including the PDPA 2023</li>
                    <li>Respond to lawful requests from courts, law enforcement, or regulatory authorities</li>
                    <li>Establish, exercise, or defend legal claims</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">4. Contact Exchange and Phone Number Sharing</h2>
              <p className="mb-3">
                Journi's core feature involves sharing phone numbers between matched drivers and riders. This section explains exactly how this works.
              </p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Phone numbers are only shared between two users when BOTH users initiate a contact exchange for a specific approved ride request.</li>
                <li>Each contact exchange deducts one credit from both users' accounts.</li>
                <li>Once a contact exchange occurs for a specific ride, both parties can view the contact details again without additional deduction.</li>
                <li>Phone numbers shared through contact exchange are for the purpose of coordinating the specific ride only.</li>
                <li>Users are prohibited from using shared contact details for any purpose other than coordinating the shared ride – including unsolicited marketing, harassment, or selling the information to third parties.</li>
                <li>Journi does not sell phone numbers or any personal data to third parties for commercial purposes.</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">5. Information Sharing and Disclosure</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">5.1 With Other Users</h3>
                  <p className="mb-2">Limited profile information is visible to other users on the Platform:</p>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Your first name and profile photo are visible to other users when viewing ride listings or ride requests.</li>
                    <li>Your gender, verified status, rating, and preferences are visible on your public profile.</li>
                    <li>Your phone number is only shared when you initiate a contact exchange as described in Section 4.</li>
                    <li>Your CNIC number, full document copies, and payment information are NEVER shared with other users.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">5.2 With Service Providers</h3>
                  <p className="mb-2">We share data with trusted third-party service providers who assist us in operating the Platform:</p>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Google LLC – Maps, Directions API, and Firebase notifications</li>
                    <li>WebSouls (hosting provider) – server infrastructure in Pakistan</li>
                    <li>Railway/Render – API infrastructure (if applicable)</li>
                    <li>All service providers are contractually required to protect your data and use it only for the purposes we specify.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">5.3 For Legal Reasons</h3>
                  <p className="mb-2">We may disclose your information if required by law or in good faith belief that such disclosure is necessary to:</p>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Comply with a legal obligation or court order under Pakistani law</li>
                    <li>Protect the rights, property, or safety of Journi, our users, or the public</li>
                    <li>Investigate fraud, security incidents, or violations of our Terms of Service</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">5.4 Business Transfers</h3>
                  <p>
                    In the event of a merger, acquisition, or sale of all or part of our assets, user information may be transferred. We will notify you via email or prominent notice on the Platform before your data is transferred and becomes subject to a different privacy policy.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">5.5 We Do Not Sell Your Data</h3>
                  <p>
                    Journi does not sell, rent, or trade your personal information to any third party for their commercial purposes. This includes phone numbers, CNIC data, location history, and ride history.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">6. Data Retention</h2>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Account data is retained for the duration of your account and for 2 years after account deletion, as required for legal compliance.</li>
                <li>Ride history is retained for 3 years for safety and dispute resolution purposes.</li>
                <li>CNIC and verification documents are retained for 5 years as required by Pakistani financial regulations.</li>
                <li>Contact exchange logs are retained for 2 years.</li>
                <li>OTP codes are deleted within 10 minutes of generation.</li>
                <li>You may request deletion of your account and associated data by emailing <a href="mailto:subscribe@journi.pk" className="text-emerald-600 hover:underline">subscribe@journi.pk</a>. Some data may be retained where legally required.</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">7. Data Security</h2>
              <p className="mb-3">We implement industry-standard security measures to protect your personal information:</p>
              <ul className="list-disc pl-6 space-y-1.5 mb-3">
                <li>All data transmission is encrypted using HTTPS/TLS</li>
                <li>Passwords are stored using bcrypt one-way hashing – we cannot retrieve your password</li>
                <li>JWT tokens used for authentication expire after one hour</li>
                <li>Database access is restricted to authorised personnel only</li>
                <li>CNIC and sensitive documents are stored with restricted access controls</li>
              </ul>
              <p>
                No method of transmission or storage is 100% secure. While we strive to protect your data, we cannot guarantee absolute security. In the event of a data breach affecting your rights, we will notify you as required by the PDPA 2023.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">8. Your Rights Under PDPA 2023</h2>
              <p className="mb-3">Under the Personal Data Protection Act 2023 of Pakistan, you have the following rights:</p>
              <ul className="list-disc pl-6 space-y-1.5 mb-3">
                <li><strong>Right to Access:</strong> Request a copy of the personal data we hold about you.</li>
                <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                <li><strong>Right to Deletion:</strong> Request deletion of your personal data, subject to legal retention requirements.</li>
                <li><strong>Right to Portability:</strong> Request your data in a machine-readable format.</li>
                <li><strong>Right to Object:</strong> Object to processing of your data for direct marketing purposes.</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for optional data processing at any time.</li>
              </ul>
              <p>
                To exercise any of these rights, contact us at <a href="mailto:subscribe@journi.pk" className="text-emerald-600 hover:underline">subscribe@journi.pk</a>. We will respond within 30 days. We may require identity verification before processing your request.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">9. Children's Privacy</h2>
              <p>
                The Journi Platform is intended for users aged 18 and above. We do not knowingly collect personal information from individuals under 18 years of age. If we become aware that we have collected data from a minor, we will delete it promptly. If you believe a minor has created an account, please contact us at <a href="mailto:subscribe@journi.pk" className="text-emerald-600 hover:underline">subscribe@journi.pk</a>.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">10. Location Data</h2>
              <p>
                Journi collects location data you enter manually when searching for or posting rides. We do not collect real-time GPS location data from your device in the background. Location data entered on the Platform is used solely for ride matching and is not used for advertising profiling.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">11. Cookies and Tracking</h2>
              <p>
                Journi is a web-based platform. We use session cookies to maintain your login state. We do not use third-party advertising cookies or tracking pixels. We do not use your data for behavioural advertising.
              </p>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on journi.pk and, where appropriate, by sending a notification to your registered phone number. Your continued use of the Platform after changes are posted constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Section 13 */}
            <section className="border-t border-gray-100 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">13. Contact Us</h2>
              <p className="mb-4">For privacy-related questions, requests, or complaints:</p>
              <div className="space-y-2 text-gray-600 bg-gray-50 p-4 rounded-lg">
                <p><strong>Email:</strong> <a href="mailto:subscribe@journi.pk" className="text-emerald-600 hover:underline">subscribe@journi.pk</a></p>
                <p><strong>WhatsApp:</strong> <a href="https://wa.me/923312312345" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">03312312345</a></p>
                <p><strong>Platform:</strong> <a href="https://journi.pk" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">journi.pk</a></p>
              </div>
              <p className="mt-3 text-sm text-gray-500">
                We aim to respond to all privacy inquiries within 5 working days.
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
}
